import PropTypes from 'prop-types';
import { Icon, Alert } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

const StatCard = ({ label, value, color, icon }) => (
  <MDBox
    flex={1}
    minWidth={110}
    p={1.5}
    sx={{ border: '1px solid', borderColor: `${color}.main`, borderRadius: 1, textAlign: 'center' }}
  >
    <Icon color={color} fontSize="small">{icon}</Icon>
    <MDTypography variant="h5" fontWeight="bold" color={color}>
      {value ?? 0}
    </MDTypography>
    <MDTypography variant="caption" color="text">{label}</MDTypography>
  </MDBox>
);

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};

StatCard.defaultProps = { value: 0 };

// Shown after a real import attempt completes — surfaces the created/updated/
// skipped counts as scannable stats, and (unlike a single generic message)
// lists every row-level error the backend reported so someone can actually
// go fix their file instead of guessing what "skipped" means.
const ImportResultSummary = ({ importResult, onReset }) => {
  if (!importResult) return null;

  const { success, message, created, updated, skipped, rowErrors } = importResult;

  return (
    <MDBox mt={2}>
      <Alert severity={success ? (rowErrors?.length ? 'warning' : 'success') : 'error'} sx={{ mb: 1.5 }}>
        {message}
      </Alert>

      {success && (
        <MDBox display="flex" gap={1.5} flexWrap="wrap" mb={rowErrors?.length ? 2 : 0}>
          <StatCard label="Created" value={created} color="success" icon="add_circle" />
          <StatCard label="Updated" value={updated} color="info" icon="autorenew" />
          <StatCard label="Skipped" value={skipped} color="warning" icon="remove_circle" />
        </MDBox>
      )}

      {rowErrors?.length > 0 && (
        <MDBox>
          <MDTypography variant="button" fontWeight="medium" color="error" display="block" mb={0.5}>
            {rowErrors.length} row{rowErrors.length === 1 ? '' : 's'} could not be saved:
          </MDTypography>
          <MDBox
            sx={{
              maxHeight: 160,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 1,
              p: 1,
            }}
          >
            {rowErrors.map((e) => (
              <MDTypography key={`${e.row}-${e.message}`} variant="caption" color="text" display="block" mb={0.25}>
                <MDTypography component="span" variant="caption" fontWeight="bold">
                  Row {e.row}:
                </MDTypography>{' '}
                {e.message}
              </MDTypography>
            ))}
          </MDBox>
        </MDBox>
      )}

      {/* Always available once there's a result — success or failure — so
          there's never a dead end where the only way to start over is to
          scroll back up and manually clear the file in Step 2. */}
      <MDButton
        variant="outlined"
        color={success ? 'info' : 'error'}
        size="small"
        sx={{ mt: 2 }}
        startIcon={<Icon>refresh</Icon>}
        onClick={onReset}
      >
        {success ? 'Import Another File' : 'Clear & Start Over'}
      </MDButton>
    </MDBox>
  );
};

ImportResultSummary.propTypes = {
  importResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    created: PropTypes.number,
    updated: PropTypes.number,
    skipped: PropTypes.number,
    rowErrors: PropTypes.arrayOf(
      PropTypes.shape({
        row: PropTypes.number,
        message: PropTypes.string,
      }),
    ),
  }),
  onReset: PropTypes.func.isRequired,
};

ImportResultSummary.defaultProps = {
  importResult: null,
};

export default ImportResultSummary;
